import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlarmGroupOuputSettingsComponent } from '@components/alarm-group-ouput-settings/alarm-group-ouput-settings.component';
import { TranslateService } from '@ngx-translate/core';
import { AlarmGroupSocketService } from '@service/alarm-group-socket.service';
import { SensorCableTagService } from '@service/sensor-cable-tag/sensor-cable-tag.service';
import {
    AlarmGroup,
    AlarmGroupConsolePayload,
    ConsoleSettingEvent,
    OutputAddress,
} from 'icreate/common/models/alarm-group';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { AlarmGroupConsoleSettingsService } from '@service/alarm-group-console-settings.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { Router } from '@angular/router';
import { Tabs } from '@src/app/pages/channels/channel.component';

@Component({
    selector: 'app-alarm-groups-console-settings',
    templateUrl: './alarm-groups-console-settings.component.html',
    styleUrl: './alarm-groups-console-settings.component.css',
    standalone: false,
})
export class AlarmGroupsConsoleSettingsComponent implements OnInit, OnDestroy {
    @Input() vessel: Vessel;
    @Input() userCanEdit: boolean;
    @ViewChild('infoMessage') infoMessage: ElementRef;

    isLoading: boolean = false;

    alarmGroups: AlarmGroup[] = [];
    vesselSlots: SensorTagSlot[] = [];
    vesselSlotsMap = new Map();
    private destroySubject$ = new Subject<void>();

    constructor(
        private alarmGroupConsoleSettingsService: AlarmGroupConsoleSettingsService,
        private alarmGroupSocketService: AlarmGroupSocketService,
        private sensorCableTagService: SensorCableTagService,
        private translate: TranslateService,
        private dialog: MatDialog,
        private messageService: MessageDialogService,
        private router: Router
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        const alarmGroupsSettings: (AlarmGroup & AlarmGroupConsolePayload)[] =
            await this.alarmGroupConsoleSettingsService.getAll(this.vessel.id);

        const slots: any[] = await this.sensorCableTagService.getVesselSlots(this.vessel.id);
        this.vesselSlotsMap = new Map<string, SensorTagSlot>();
        if (slots?.length > 0) {
            this.vesselSlots = slots.map(slot => {
                const vesselSlot = {
                    no: slot.no,
                    slotId: slot.id,
                    ioType: slot.ioType,
                    fcuNum: slot.FcuUnit?.index,
                    fcuId: slot.FcuUnit?.id,
                    fuNum: slot.FuUnit?.index,
                    fuId: slot.FuUnit?.id,
                    terminal: slot.terminal,
                    vesselId: this.vessel.id,
                };
                this.vesselSlotsMap.set(vesselSlot.slotId, vesselSlot);
                return vesselSlot;
            });
        }

        this.alarmGroups = alarmGroupsSettings.map(group => {
            if (group.ouputs?.length > 0) {
                group.outputSettings = this.buildGroupSettings(group.ouputs);
            }
            delete group.ouputs;
            return group;
        });

        this.alarmGroupSocketService
            .alarmGroupsUpdated$()
            .pipe(takeUntil(this.destroySubject$))
            .subscribe(({ alarmGroups, vesselId }) => {
                if (vesselId === this.vessel.id) {
                    this.updateGroupSettings(alarmGroups);
                }
            });

        this.alarmGroupConsoleSettingsService
            .alarmSettingUpdated$()
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((updatedSettings: AlarmGroupConsolePayload) => {
                if (updatedSettings.vesselId === this.vessel.id) {
                    this.updateGroupSettings(updatedSettings);
                }
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }

    async openSettings(alarmGroup: AlarmGroup) {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(AlarmGroupOuputSettingsComponent, {
            width: '474px',
            data: {
                vessel: this.vessel,
                alarmGroup: alarmGroup,
                vesselSlots: this.vesselSlots,
            },
        });

        const updatedSettings = await lastValueFrom(dialogRef.afterClosed());
        if (updatedSettings) alarmGroup.outputSettings = updatedSettings;
    }

    getSettingsString(alarmGroup: AlarmGroup) {
        if (!alarmGroup.outputSettings) return '-';
        const settings = alarmGroup.outputSettings;
        let outputString = this.translate.instant('alarm-groups-settings.table-row.output');
        if (settings.length > 1) {
            outputString = this.translate.instant('alarm-groups-settings.table-row.output-plural');
        }
        return `${settings.length} ${outputString}`;
    }

    updateList(updatedList: AlarmGroup[]) {
        if (updatedList?.length > 0 && updatedList[0].vesselId === this.vessel.id) {
            const currentGroups = _.cloneDeep(this.alarmGroups);
            this.alarmGroups = updatedList.map(group => {
                const oldGroup = currentGroups.find(g => g.id == group.id);
                const settings = _.cloneDeep(oldGroup.outputSettings);
                group.outputSettings = settings;
                return group;
            });
        } else {
            this.alarmGroups = [];
        }
    }

    updateGroupSettings(settings: AlarmGroupConsolePayload) {
        const alarmGroupId = settings?.alarmGroupId;
        if (!alarmGroupId) return;
        const group = this.alarmGroups.find(group => group.id == alarmGroupId);
        group.outputSettings = this.buildGroupSettings(settings.ouputs);
        if (settings.event == ConsoleSettingEvent.CREATING) {
            this.messageService.openMessage('alarm-groups-settings.notification.set', {
                name: group.name,
            });
        } else if (settings.event == ConsoleSettingEvent.UPDATING) {
            this.messageService.openMessage('alarm-groups-settings.notification.edit', {
                name: group.name,
            });
        }
    }

    buildGroupSettings(outputs: AlarmGroupConsolePayload['ouputs'] | undefined): OutputAddress[] {
        const outputSettings: OutputAddress[] = [];

        for (const setting of outputs) {
            const slot = this.vesselSlotsMap.get(setting?.slotId);
            if (slot)
                outputSettings.push({
                    index: setting.index,
                    type: setting.type,
                    mode: setting.mode,
                    signal: setting.signal,
                    address: {
                        fcuNum: slot.fcuNum,
                        fuNum: slot.fuNum,
                        slotNum: slot.no,
                        terminal: setting.terminalNum,
                        settingId: setting.id,
                    },
                });
        }

        return outputSettings;
    }

    openAlarmsGroupPage() {
        this.router.navigateByUrl(`channels/${this.vessel.shipyardId}/${this.vessel.id}`, {
            state: { origin: Tabs.ALARM_GROUP },
        });
    }
}
